import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  HolderOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Button, Space, Spin, Upload, UploadFile } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { DragEvent } from "react";
import { classNames } from "src/common/classNames";
import { urlForDownload } from "src/common/upload/urlForDownload";

import { MdmUploadStore } from "../MdmUploadStore";
import { UploadInfo } from "../UploadInfo";
import * as styles from "./UploaderDragger.module.less";

const { Dragger } = Upload;

export interface PropsUploaderDragger {
  store: MdmUploadStore;
}

export const UploaderDragger: React.FC<PropsUploaderDragger> = observer(
  ({ store }) => {
    const { isImage, intFiles, maxCount, disabled, beforeUpload, antUpload } =
      store;
    const { accept } = store.params;
    const [dragIndex, setDragIndex] = React.useState<number | null>(null);
    const [dragList, setDragList] = React.useState<UploadFile[]>(intFiles);

    const onDelete = (index: number) => {
      store.remove(index);
    };

    React.useEffect(() => {
      setDragList(intFiles);
    }, [intFiles]);

    const onDragStart = (index: number) => () => {
      setDragIndex(index);
    };

    const onDragOver = (index: number) => (event: DragEvent<HTMLLIElement>) => {
      event.preventDefault();
      if (dragIndex === null) return;
      const dragOverItemIndex = index;
      if (dragIndex === dragOverItemIndex) {
        return;
      }
      const itemsCopy = [...intFiles];
      const itemToMove = itemsCopy.splice(dragIndex, 1)[0];
      if (itemToMove) {
        itemsCopy.splice(dragOverItemIndex, 0, itemToMove);
        setDragList(itemsCopy);
      }
    };

    const onDragEnd = () => {
      setDragIndex(null);
      store.setIntFiles(dragList);
    };
    const actionsDisabled =
      intFiles.some(({ status }) => status === "uploading") || disabled;

    const draggable =
      !intFiles.find(
        ({ status: itemStatus }) =>
          itemStatus === "error" || itemStatus === "uploading",
      ) && !disabled;

    return (
      <div className={styles.draggerWrapper}>
        <Dragger
          className={classNames([
            styles.modalUpload,
            [(intFiles?.length ?? 0) >= (maxCount ?? 1), styles.uploadDisabled],
          ])}
          listType="picture"
          accept={accept}
          fileList={intFiles}
          onChange={({ file, fileList }) => {
            if (!file.url && file.status === "done") {
              // eslint-disable-next-line no-param-reassign
              file.url = urlForDownload(store.getExtId(file.uid), isImage);
            }
            store.setIntFiles(fileList);
          }}
          onRemove={(file) => store.removeByUid(file.uid)}
          disabled={disabled}
          maxCount={maxCount}
          multiple
          beforeUpload={beforeUpload}
          customRequest={antUpload}
        >
          <div className={styles.dragger}>
            <FileOutlined className={styles.draggerIcon} />
            <div>{t("Click or drag the file/s to the area to upload")}</div>
            <UploadInfo store={store} />
          </div>
        </Dragger>
        <ul className={styles.fileList}>
          {dragList.map((file, index) => (
            <li
              key={file.uid}
              className={classNames([
                styles.listItemTitle,
                [file.status === "error", styles.listItemTitleErr],
                [!draggable, styles.listItemNotDraggable],
              ])}
              draggable={draggable}
              onDragStart={onDragStart(index)}
              onDragOver={onDragOver(index)}
              onDragEnd={onDragEnd}
              title={JSON.stringify(file)}
            >
              <div className={styles.listItemInfo}>
                {draggable && <HolderOutlined />}
                {file.status === "done" && (
                  <>
                    {isImage && (
                      <img
                        key={file.uid}
                        className={styles.listItemImg}
                        src={file.url ?? file.thumbUrl}
                        alt=""
                      />
                    )}
                    <div className={styles.listItemName}>{file.name}</div>
                  </>
                )}
                {file.status === "error" && (
                  <>
                    {isImage && (
                      <PictureOutlined
                        className={classNames([
                          styles.listItemInfoErr,
                          styles.infoErrImage,
                        ])}
                      />
                    )}
                    {!isImage && (
                      <FileOutlined className={styles.listItemInfoErr} />
                    )}
                    <div className={styles.listItemName}>{file.name}</div>
                  </>
                )}
                {file.status === "uploading" && (
                  <Space>
                    <Spin />
                    <span className={styles.listItemName}>{file.name}</span>
                  </Space>
                )}
              </div>
              <div className={styles.listItemControl}>
                {file.url && file.status === "done" && (
                  <Button
                    icon={<DownloadOutlined />}
                    href={file.url}
                    disabled={actionsDisabled}
                    type="text"
                    download={file.name}
                  />
                )}
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    onDelete(index);
                  }}
                  type="text"
                  disabled={actionsDisabled}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  },
);
